import { on, unByKey } from '@disy/lib/event-util';
import { clamp } from '@disy/lib/math';
import { px } from '@disy/lib/px';
import { throttle } from '@disy/lib/throttle';

import { getStore } from 'cadenza/store/cadenza-store';
import { ViewType } from 'cadenza/workbook/api/view-type';
import { getActiveWorksheetId } from 'cadenza/workbook/workbook-core-selectors.state';
import {
  GRID_SIZE,
  VIEW_MARGIN,
  dispatchLayoutChangeEvent,
  getGridArea,
  intersectsRect,
  startAdjusting,
  stopAdjusting,
  toggleViewBorder,
} from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';
import { calculateWorksheetViewBorders } from 'cadenza/workbook/worksheet/worksheet-util';

export const MIN_VIEW_SIZE_IN_CELLS = 4;
const SMALLER_MIN_VIEW_SIZE_IN_CELLS = 2;
export const VIEW_TYPES_WITH_SMALLER_MIN_SIZE = [ViewType.TEXT, ViewType.IMAGE];
const RESIZING_VIEW_CSS_CLASS = 'is-resizing';

/**
 * Temporary class that gets added after a resize action and removed again right after in a
 * temporarily added click listener between the resize action's pointerup and click event to allow
 * detection of a resize action in the click event listener of the same mouse interaction.
 */
export const VIEW_WAS_RESIZED_CSS_CLASS = 'was-resized';

export function handleViewResizing(pointerDownEvent, view, grid, viewRect, gridRect) {
  const { clientX: x, clientY: y } = pointerDownEvent;
  const resize = view.dResize;
  const colWidth = gridRect.width / GRID_SIZE;
  const rowHeight = gridRect.height / GRID_SIZE;
  const minimalSizeInCells =
    VIEW_TYPES_WITH_SMALLER_MIN_SIZE.includes(view.type) ? SMALLER_MIN_VIEW_SIZE_IN_CELLS : MIN_VIEW_SIZE_IN_CELLS;
  const minWidth = minimalSizeInCells * colWidth;
  const minHeight = minimalSizeInCells * rowHeight;
  const position = startAdjusting(grid, view, RESIZING_VIEW_CSS_CLASS);

  // prevent resizing view to be smaller than minimum size
  const clampMaxResizeTop = viewRect.bottom - gridRect.top - minHeight + 2 * VIEW_MARGIN;
  const clampMaxResizeRight = gridRect.right - viewRect.left - minWidth + 2 * VIEW_MARGIN;
  const clampMaxResizeBottom = gridRect.bottom - viewRect.top - minHeight + 2 * VIEW_MARGIN;
  const clampMaxResizeLeft = viewRect.right - gridRect.left - minWidth + 2 * VIEW_MARGIN;

  const otherViews = grid.views.filter((v) => v !== view);
  const otherViewsBounds = otherViews.map((v) => v.getBoundingClientRect());

  const eventKeys = [
    on(
      window,
      'pointermove',
      throttle(
        (event) => {
          const { clientX: newX, clientY: newY } = event;
          const multi = (resize.top || resize.bottom) && (resize.left || resize.right);
          const dx = newX - x;
          const dy = newY - y;
          const xIsMain = dx ** 2 > dy ** 2;
          const resizingRect = view.getBoundingClientRect();

          /*
           * Special case is resizing views from corners. We have movement in 2 directions in this case
           * We calculate the delta x (dx) and delta y (dy) from pointerdown coordinates to pointermove coordinates.
           * This way the resizing view will extend as mush as possible in direction of dx or dy
           * depending value one is higher.
           */
          if (multi) {
            if (xIsMain) {
              if (resize.right) {
                resizeRight();
              } else {
                resizeLeft();
              }
              if (resize.top) {
                resizeTop();
              } else {
                resizeBottom();
              }
            } else {
              if (resize.top) {
                resizeTop();
              } else {
                resizeBottom();
              }
              if (resize.right) {
                resizeRight();
              } else {
                resizeLeft();
              }
            }
          } else if (resize.top) {
            resizeTop();
          } else if (resize.right) {
            resizeRight();
          } else if (resize.bottom) {
            resizeBottom();
          } else if (resize.left) {
            resizeLeft();
          }

          grid.toggleDropPreviewVisibility(true);
          grid.setDropPreviewLayout(getGridArea(view, gridRect.width, gridRect.height));
          otherViews.forEach((v) => toggleViewBorder(v, true));

          /*
      When resizing to the top, we take current client bounding rectangle and set its
      top value to be top edge of the grid. After that we check for the intersections with other views
      and find closest one which blocks further resizing to the top.
      (it will be the bottom edge of upper view that blocks resizing in top direction)
       */
          function resizeTop() {
            const { right, bottom, left } = resizingRect;
            const minTopAbs = otherViewsBounds.reduce((result, rect) => {
              if (intersectsRect(rect, { top: gridRect.top, right, bottom, left })) {
                return Math.max(result, rect.bottom);
              }
              return result;
            }, gridRect.top);

            let minTop = minTopAbs - gridRect.top;
            minTop += minTop ? 2 * VIEW_MARGIN : VIEW_MARGIN;
            view.style.top = px(clamp(position.top + (newY - y), minTop, clampMaxResizeTop));
          }

          /*
      When resizing to the right, we take current client bounding rectangle and set its
      right value to be right edge of the grid. After that we check for the intersections with other views
      and find closest one which blocks further resizing to the right.
      (it will be the left edge of view in the right that blocks resizing in right direction)
       */
          function resizeRight() {
            const { top, bottom, left } = resizingRect;
            const minRightAbs = otherViewsBounds.reduce((result, rect) => {
              if (intersectsRect(rect, { top, right: gridRect.right, bottom, left })) {
                return Math.min(result, rect.left);
              }
              return result;
            }, gridRect.right);

            let minRight = gridRect.right - minRightAbs;
            minRight += minRight ? 2 * VIEW_MARGIN : VIEW_MARGIN;
            view.style.right = px(clamp(position.right - (newX - x), minRight, clampMaxResizeRight));
          }

          /*
      When resizing to the bottom, we take current client bounding rectangle and set its
      bottom value to be bottom edge of the grid. After that we check for the intersections with other views
      and find closest one which blocks further resizing to the bottom.
      (it will be the top edge of view bellow that blocks resizing in bottom direction)
       */
          function resizeBottom() {
            const { top, right, left } = resizingRect;
            const minBottomAbs = otherViewsBounds.reduce((result, rect) => {
              if (intersectsRect(rect, { top, right, bottom: gridRect.bottom, left })) {
                return Math.min(result, rect.top);
              }
              return result;
            }, gridRect.bottom);

            let minBottom = gridRect.bottom - minBottomAbs;
            minBottom += minBottom ? 2 * VIEW_MARGIN : VIEW_MARGIN;
            view.style.bottom = px(clamp(position.bottom - (newY - y), minBottom, clampMaxResizeBottom));
          }

          /*
      When resizing to the left, we take current client bounding rectangle and set its
      left value to be left edge of the grid. After that we check for the intersections with other views
      and find closest one which blocks further resizing to the left.
      (it will be the right edge of left view that blocks resizing in left direction)
       */
          function resizeLeft() {
            const { top, right, bottom } = resizingRect;
            const minLeftAbs = otherViewsBounds.reduce((result, rect) => {
              if (intersectsRect(rect, { top, right, bottom, left: gridRect.left })) {
                return Math.max(result, rect.right);
              }
              return result;
            }, gridRect.left);

            let minLeft = minLeftAbs - gridRect.left;
            minLeft += minLeft ? 2 * VIEW_MARGIN : VIEW_MARGIN;
            view.style.left = px(clamp(position.left + (newX - x), minLeft, clampMaxResizeLeft));
          }
        },
        40,
        { trailing: false },
      ),
    ),
    on(window, 'pointerup', () => {
      unByKey(eventKeys);
      view.setLayoutOnClientAndServer(getGridArea(view, gridRect.width, gridRect.height));
      stopAdjusting(grid, view, RESIZING_VIEW_CSS_CLASS);

      // For detection of resize actions in subsequent events (e.g. click) of the same mouse
      // interaction.
      view.classList.add(VIEW_WAS_RESIZED_CSS_CLASS);
      setTimeout(() => view.classList.remove(VIEW_WAS_RESIZED_CSS_CLASS));
      grid.toggleDropPreviewVisibility(false);
      const showBorders = calculateWorksheetViewBorders(
        getActiveWorksheetId(getStore().getState()),
        getStore().getState(),
      );
      if (!showBorders) {
        otherViews.forEach((v) => toggleViewBorder(v, false));
      }
      dispatchLayoutChangeEvent(view);
    }),
  ];
}
